import { StyleSheet, Document, Page, Text, View } from '@react-pdf/renderer'
import React from 'react'
import HeaderOfPDFFashion from './Header'
import FooterOfPDFFashion from './Footer'
import { SectionTitle } from '../SectionTitle'
import MainFieldsOfPDF from '../MainFields'
import type {
  FashionOrderDetailMapped,
  ProductApiData,
} from '../../../pages/ClientPanel/FashionOrders/fashionOrderDetailFromApi'
import {
  countColors,
  formatCurrency,
  formatProductDiscountLabel,
  getTotalPieces,
} from '../../../pages/ClientPanel/FashionOrders/fashionOrderDetailFromApi'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    paddingTop: 122,
    paddingBottom: 100,
    paddingHorizontal: 40,
  },
  pageNumber: {
    position: 'absolute',
    top: 36,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 9,
    color: '#000',
  },
  pageTitle: {
    position: 'absolute',
    top: 76,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 13,
    fontWeight: 'bold',
    color: '#000',
  },
  section: {
    marginBottom: 14,
    paddingBottom: 10,
    borderBottom: '1px solid #000',
  },
  subsectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 6,
    color: '#000',
  },
  itemRow: {
    marginBottom: 10,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#f5f5f5',
    borderRadius: 3,
    border: '1px solid #000',
  },
  kitHeader: {
    marginTop: 8,
    marginBottom: 6,
    paddingVertical: 4,
    borderBottom: '1px solid #ccc',
  },
  kitTitle: {
    fontSize: 10,
    fontWeight: 'bold',
  },
  kitSummaryLine: {
    fontSize: 8,
    marginTop: 2,
    color: '#000',
  },
})

function productToFields(product: ProductApiData) {
  const totalPieces = getTotalPieces(product)
  return [
    { label: 'Produto', value: product.fashionProduct?.name ?? '-' },
    {
      label: 'Família',
      value: product.fashionProduct?.group?.family?.name || product.family?.name || '-',
    },
    { label: 'Cores', value: String(countColors(product)) },
    {
      label: 'Preço unit.',
      value:
        typeof product.fashionProduct?.price === 'number'
          ? formatCurrency(product.fashionProduct.price)
          : '-',
    },
    { label: 'Qtd. peças', value: String(totalPieces) },
    { label: 'Desconto', value: formatProductDiscountLabel(product) },
    {
      label: 'Subtotal',
      value: typeof product.subtotal === 'number' ? formatCurrency(product.subtotal) : '-',
    },
  ]
}

interface BodyFashionOrderProps {
  orderId: number
  detail: FashionOrderDetailMapped | null
  qrCodeDataUrl?: string
}

const BodyFashionOrder: React.FC<BodyFashionOrderProps> = ({ orderId, detail, qrCodeDataUrl }) => {
  if (!detail) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <HeaderOfPDFFashion />
          <Text
            fixed
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          />
          <Text fixed style={styles.pageTitle}>{`Pedido fashion #${orderId}`}</Text>
          <Text style={{ fontSize: 10 }}>Sem dados do pedido.</Text>
          <FooterOfPDFFashion qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={String(orderId)} />
        </Page>
      </Document>
    )
  }

  const { formState, addedProducts, kitCount } = detail

  const participantesFields = [
    { label: 'Fornecedor', value: formState.supplier.companyName || '—' },
    { label: 'Vendedor', value: formState.seller.name || '—' },
    { label: 'Cliente', value: formState.company.companyName || '—' },
  ]

  const formatDateBR = (dateStr: string | undefined | null) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  const detalhesFields = [
    { label: 'Tipo de pedido', value: formState.orderType || '—' },
    { label: 'Data início da entrega', value: formatDateBR(formState.startDateOfDelivery) },
    { label: 'Data final da entrega', value: formatDateBR(formState.finalDateOfDelivery) },
    { label: 'Condição de pagamento', value: formState.paymentCondition || '—' },
    { label: 'Tipo de frete', value: formState.shippingType || '—' },
    { label: 'Valor do frete', value: formState.shippingCost || '—' },
    { label: 'Código de rastreio', value: formState.trackingCode || '—' },
    { label: 'Valor máx. faturamento', value: formState.maximumBillingAmount || '—' },
    { label: 'Informações adicionais', value: formState.extraInformation || '—' },
  ]

  const resumoFields = [
    { label: 'Conjuntos', value: String(kitCount) },
    { label: 'Itens', value: formState.quantityItems },
    { label: 'Peças', value: formState.quantityPieces },
    { label: 'Total', value: formState.totalCost },
  ]

  const standaloneProducts = addedProducts
    .map((p, i) => ({ product: p, index: i }))
    .filter(({ product }) => product.kitIndex === null || product.kitIndex === undefined)

  const kitProducts = (kitIdx: number) =>
    addedProducts
      .map((p, i) => ({ product: p, index: i }))
      .filter(({ product }) => product.kitIndex === kitIdx)

  const renderProductBlock = (product: ProductApiData, key: string) => (
    <View key={key} style={styles.itemRow} wrap={false}>
      <MainFieldsOfPDF infoFields={productToFields(product)} />
    </View>
  )

  const hasProductSection = standaloneProducts.length > 0 || kitCount > 0

  const productsPage = (
    <Page size="A4" style={styles.page}>
      <HeaderOfPDFFashion />
      <Text
        fixed
        style={styles.pageNumber}
        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
      />
      <Text fixed style={styles.pageTitle}>{`Pedido fashion #${orderId}`}</Text>
      <SectionTitle title="Produtos" />
      {!hasProductSection && (
        <Text style={{ fontSize: 9, color: '#666', marginBottom: 8 }}>Nenhum produto cadastrado.</Text>
      )}
      {standaloneProducts.map(({ product, index }) =>
        renderProductBlock(product, `s-${product.fashionProductId}-${index}`),
      )}

      {Array.from({ length: kitCount }, (_, kitIdx) => {
        const kits = kitProducts(kitIdx)
        const kitTotalPieces = kits.reduce((sum, { product }) => sum + getTotalPieces(product), 0)
        const kitTotal = kits.reduce((sum, { product }) => sum + (product.subtotal || 0), 0)
        return (
          <View key={`kit-${kitIdx}`} wrap={false}>
            <View style={styles.kitHeader}>
              <Text style={styles.kitTitle}>Conjunto {kitIdx + 1}</Text>
              <Text style={styles.kitSummaryLine}>
                Itens: {kits.length} | Peças: {kitTotalPieces} | Total: {formatCurrency(kitTotal)}
              </Text>
            </View>
            {kits.map(({ product, index }) =>
              renderProductBlock(product, `k-${kitIdx}-${product.fashionProductId}-${index}`),
            )}
          </View>
        )
      })}
      <FooterOfPDFFashion qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={String(orderId)} />
    </Page>
  )

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <HeaderOfPDFFashion />
        <Text
          fixed
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
        />
        <Text fixed style={styles.pageTitle}>{`Pedido fashion #${orderId}`}</Text>

        <View style={styles.section}>
          <Text style={styles.subsectionTitle}>Participantes</Text>
          <MainFieldsOfPDF infoFields={participantesFields} />
        </View>

        <View style={styles.section}>
          <Text style={styles.subsectionTitle}>Detalhes do pedido</Text>
          <MainFieldsOfPDF infoFields={detalhesFields} />
        </View>

        <View style={styles.section}>
          <Text style={styles.subsectionTitle}>Resumo</Text>
          <MainFieldsOfPDF infoFields={resumoFields} />
        </View>

        <FooterOfPDFFashion qrCodeDataUrl={qrCodeDataUrl} qrCodeValue={String(orderId)} />
      </Page>

      {hasProductSection ? productsPage : null}
    </Document>
  )
}

export default BodyFashionOrder
